/* THE fund choice — one control, one scope, everywhere (Ale ruling
 * 2026-09-05, Tier-2 "one fund selector everywhere").
 *
 * A fund used to be reached four unrelated ways — the Workbench fund
 * chips, Today's per-fund doors, the Funds list, and a "switch fund" menu
 * inside the record-keeping queue — and the choice never travelled: the
 * officer picked a fund on the landing, entered a queue, and was back at
 * every fund without being told. ONE selector now writes ONE scope:
 *
 *   · the URL carries it (`?fund=<id>` on Today, the Workbench landing
 *     and every queue page) so a link the officer shares scopes the same
 *     way it looked;
 *   · the LAST CHOICE is persisted, so a page opened with no `?fund=` of
 *     its own adopts it — the fund follows the officer across pages until
 *     he clears it;
 *   · "All funds" is the default AND the clear: it stores the cleared
 *     sentinel, so a bare URL after clearing stays at all funds instead
 *     of silently re-scoping to the fund before it.
 *
 * The fund PAGE keeps the fund in its path (/v2/funds/<id>) — there the
 * selector navigates instead of writing a param, and still records the
 * choice here so the queues the officer opens next carry it.
 */

import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiFetch } from '../lib/api'
import { useAuthSession } from '../lib/authSession'

const KEY = 'nolos.fund'
/** The stored value meaning "cleared" — never a fund id. */
const CLEARED = 'all'

/** The last fund chosen, or null when the choice is cleared / unknown.
 *  Storage can throw (private mode, a locked-down browser): no scope. */
export function lastFundChoice(): string | null {
  try {
    const v = localStorage.getItem(KEY)
    return v && v !== CLEARED ? v : null
  } catch {
    return null
  }
}

/** Record the choice. `null` = All funds, stored so it survives a reload. */
export function rememberFundChoice(id: string | null): void {
  try {
    localStorage.setItem(KEY, id ?? CLEARED)
  } catch {
    /* no storage — the URL alone carries the scope this session */
  }
}

/* The pages that carry their own window-level J/K/Enter walk (the
 * Workbench tiles, a queue's rows) must stand down while the selector's
 * menu owns the keyboard. A module counter, because those handlers sit on
 * `window` and cannot be reached by React's own propagation. */
let openMenus = 0

/** Is a fund menu standing? Every window-level key handler asks first. */
export function fundMenuOpen(): boolean {
  return openMenus > 0
}

/** The selector calls this on open (+1) and on close (−1). */
export function noteFundMenu(delta: 1 | -1): void {
  openMenus = Math.max(0, openMenus + delta)
}

export interface FundScope {
  /** The fund in scope, or null = All funds. */
  fundId: string | null
  /** Choose a fund (null = All funds): the URL and the stored choice. */
  setFund: (id: string | null) => void
}

/** The scope on a page that carries the fund as `?fund=`. */
export function useFundScope(): FundScope {
  const [params, setParams] = useSearchParams()
  const fundId = params.get('fund') || null
  const named = params.has('fund')
  const contextual = params.has('scope')
  const session = useAuthSession()

  /* Arriving with a fund named (a door from Today, a shared link) IS a
   * choice — remember it. Arriving with none, adopt the last one: the
   * fund follows the officer. Writing it into the URL (replace) keeps
   * ONE source of truth, so every reader on the page agrees. */
  useEffect(() => {
    // Explicit context links own their complete scope. Legacy remembered
    // preferences must never add a narrower fund to an all/client view.
    if (contextual || !session) return
    if (fundId) {
      rememberFundChoice(fundId)
      return
    }
    if (named) {
      // An explicit empty fund param is a shareable All funds choice.
      rememberFundChoice(null)
      return
    }
    const last = lastFundChoice()
    if (!last) return
    const controller = new AbortController()
    // A wipe or access change can invalidate a remembered id. Only adopt it
    // after a successful current fund-list read; failure is not existence.
    apiFetch<{ id: string }[]>('/api/funds', {
      signal: controller.signal,
      background: true,
    })
      .then((funds) => {
        if (controller.signal.aborted || lastFundChoice() !== last || !Array.isArray(funds)) return
        if (!funds.some((fund) => fund.id === last)) {
          rememberFundChoice(null)
          return
        }
        setParams(
          (prev) => {
            if (prev.has('scope') || prev.has('fund')) return prev
            const next = new URLSearchParams(prev)
            next.set('fund', last)
            return next
          },
          { replace: true }
        )
      })
      .catch(() => {
        // Keep the preference for a later successful read, without applying
        // an unverified scope or hiding the page's own request errors.
      })
    return () => controller.abort()
  }, [contextual, fundId, named, session, setParams])

  const setFund = useCallback(
    (id: string | null) => {
      rememberFundChoice(id)
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (id) next.set('fund', id)
          else next.delete('fund')
          if (next.has('scope')) {
            next.set('scope', id ? 'fund' : 'all')
            next.delete('client')
            next.delete('subchain')
            next.delete('below')
          }
          // A selected row / focused object belonged to the OLD scope.
          next.delete('task')
          next.delete('focus')
          return next
        },
        { replace: true }
      )
    },
    [setParams]
  )

  return { fundId, setFund }
}
